import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Clock, Lock, MessageSquare, Send, X } from "lucide-react";
import type { FieldDef, RecordData } from "../core";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { useSession } from "../react/providers/SessionProvider";
import { useOptionalFormStateContext } from "../react/form/RecordProvider";
import { uidLabel } from "./quickMetaHelpers";
import { nameSearchDomain, PARTNER_SEARCH_FIELDS, USER_SEARCH_FIELDS } from "./chatterUserNames";

// Record composer (communication Enh 03 Slice 5) — the slash composer on top
// of the unified timeline, per the frozen mockup:
//   plain text       → message (notifies followers)
//   /note …          → 🔒 internal note (never notifies — engine rule)
//   /activity …      → expands the inline schedule panel (type · subject ·
//                      assignee · due date)
//   @name            → plain mention token; the SERVER's mention parser
//                      resolves it and subscribes the user — no markup needed.
//
// Mode gating mirrors the <activity> components: the composer renders only
// when MessageConversation survived gating; the /activity mode additionally
// needs ScheduledActivity.

export type ComposerMode = "message" | "note" | "activity";

/** Parse the input's leading slash token into (mode, body-without-prefix). */
export function detectComposerMode(text: string): { mode: ComposerMode; body: string } {
    if (text.startsWith("/note")) {
        return { mode: "note", body: text.slice("/note".length).trimStart() };
    }
    if (text.startsWith("/activity")) {
        return { mode: "activity", body: text.slice("/activity".length).trimStart() };
    }
    return { mode: "message", body: text };
}

export interface SlashOption {
    token: string;
    label: string;
    hint: string;
}

/** Slash-palette options matching the input's current "/prefix". Open while
    the text is a bare slash token (no space yet); empty otherwise. */
export function slashSuggestions(text: string, canSchedule: boolean): SlashOption[] {
    if (!/^\/[a-z]*$/i.test(text)) return [];
    const all: SlashOption[] = [
        { token: "/note", label: "Internal note", hint: "followers are NOT notified" },
        ...(canSchedule
            ? [{ token: "/activity", label: "Schedule activity", hint: "type · subject · assignee · due date" }]
            : []),
    ];
    return all.filter((o) => o.token.startsWith(text.toLowerCase()));
}

// ── @mention picker ─────────────────────────────────────────────────────────
// Trailing "@token" opens a grouped picker (user-specified order):
//   1. the record's FOLLOWERS
//   2. ── separator ── partners referenced by THIS record's res.partner fields
//   3. ── separator ── the directory: internal users first, then partners.
// Typing after @ filters every group. Picking inserts the partner's EMAIL
// (unambiguous for the server-side resolver) or the name's first token.

export type MentionGroup = "followers" | "record" | "users" | "partners";

export interface MentionOption {
    key: string;
    group: MentionGroup;
    label: string;
    sub?: string;
    /** Token inserted after "@" — email preferred, else first name word. */
    insert: string;
}

/** The active "@query" at the end of the input, or null when no picker. */
export function mentionQuery(text: string): string | null {
    const m = /(?:^|\s)@([^\s@]*)$/.exec(text);
    return m ? m[1] : null;
}

/** Replace the trailing "@query" with the picked token. */
export function insertMention(text: string, insert: string): string {
    return text.replace(/@[^\s@]*$/, `@${insert} `);
}

export function mentionInsertFor(name: string, email?: string | null): string {
    if (email && email.includes("@")) return email;
    return (name || "").trim().split(/\s+/)[0] ?? "";
}

/** Case-insensitive substring filter across every group, order preserved. */
export function filterMentionOptions(options: MentionOption[], query: string): MentionOption[] {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
        (o) => o.label.toLowerCase().includes(q) || o.insert.toLowerCase().includes(q),
    );
}

/** The collective option pinned FIRST — mentions every follower at once. */
export const FOLLOWERS_ALL_KEY = "__followers__";

export interface MentionChip {
    key: string;
    label: string;
    /** Token(s) source — FOLLOWERS_ALL_KEY expands to every follower. */
    insert: string;
}

/** The composer body is a SEGMENT LIST so mention chips stay inline exactly
    where they were typed ("Hi this is test [@hari] …"). Typing always edits
    the tail; picking a mention freezes the typed text and appends a chip. */
export type ComposerSegment =
    | { kind: "text"; text: string }
    | { kind: "chip"; chip: MentionChip };

/** Final outgoing body: segments flattened in order (chips → "@token", the
    collective chip expands to every follower), then the live tail text. The
    server-side mention parser does the rest. */
export function assembleBody(segments: ComposerSegment[], tail: string, followerInserts: string[]): string {
    const parts: string[] = [];
    const seen = new Set<string>();
    for (const segment of segments) {
        if (segment.kind === "text") {
            if (segment.text.trim()) parts.push(segment.text.trim());
            continue;
        }
        const expanded = segment.chip.insert === FOLLOWERS_ALL_KEY ? followerInserts : [segment.chip.insert];
        for (const token of expanded) {
            if (token && !seen.has(token)) {
                seen.add(token);
                parts.push(`@${token}`);
            }
        }
    }
    if (tail.trim()) parts.push(tail.trim());
    return parts.join(" ");
}

const MENTION_GROUP_LABELS: Record<MentionGroup, string> = {
    followers: "Followers",
    record: "On this record",
    users: "Users",
    partners: "Partners",
};

export interface ActivityTypeOption {
    code: string;
    name: string;
}

export interface ActivityDraft {
    typeCode: string;
    subject: string;
    dueDate: string;
    assigneeUuid: string;
    assigneeLabel: string;
}

interface RecordComposerViewProps {
    canSchedule: boolean;
    text: string;
    mode: ComposerMode;
    sending: boolean;
    /** Filtered, group-ordered @mention options (empty → closed). */
    mentionOptions?: MentionOption[];
    onPickMention?: (option: MentionOption) => void;
    /** Frozen body segments — text + mention chips, rendered INLINE in the
        order they were typed; the input edits only the tail. */
    segments?: ComposerSegment[];
    onRemoveSegment?: (index: number) => void;
    /** Backspace on an empty tail — pops the last segment (chip removed;
        text re-opens into the input). */
    onPopSegment?: () => void;
    onTextChange: (text: string) => void;
    onSend: () => void;
    onCancelActivity: () => void;
    /* activity panel */
    activityTypes: ActivityTypeOption[];
    draft: ActivityDraft;
    onDraftChange: (draft: ActivityDraft) => void;
    assigneeQuery: string;
    onAssigneeQueryChange: (q: string) => void;
    assigneeResults: Array<{ uuid: string; label: string }>;
    onPickAssignee: (uuid: string, label: string) => void;
    onScheduleActivity: () => void;
}

export function RecordComposerView({
    canSchedule,
    text,
    mode,
    sending,
    mentionOptions = [],
    onPickMention,
    segments = [],
    onRemoveSegment,
    onPopSegment,
    onTextChange,
    onSend,
    onCancelActivity,
    activityTypes,
    draft,
    onDraftChange,
    assigneeQuery,
    onAssigneeQueryChange,
    assigneeResults,
    onPickAssignee,
    onScheduleActivity,
}: RecordComposerViewProps): ReactNode {
    const [paletteIndex, setPaletteIndex] = useState(0);
    if (mode === "activity") {
        const valid = draft.typeCode && draft.subject.trim();
        return (
            <div className="record-composer record-composer--activity" data-testid="composer-activity">
                <div className="record-composer__activity-head">
                    <Clock size={13} aria-hidden="true" />
                    Schedule activity
                    <button
                        type="button"
                        className="record-composer__icon-btn"
                        onClick={onCancelActivity}
                        aria-label="Cancel activity"
                    >
                        <X size={13} />
                    </button>
                </div>
                <div className="record-composer__activity-grid">
                    <label>
                        Type
                        <select
                            value={draft.typeCode}
                            onChange={(e) => onDraftChange({ ...draft, typeCode: e.target.value })}
                            aria-label="Activity type"
                        >
                            <option value="">Select…</option>
                            {activityTypes.map((t) => (
                                <option key={t.code} value={t.code}>{t.name}</option>
                            ))}
                        </select>
                    </label>
                    <label>
                        Subject
                        <input
                            value={draft.subject}
                            onChange={(e) => onDraftChange({ ...draft, subject: e.target.value })}
                            placeholder="What needs to happen?"
                            aria-label="Activity subject"
                        />
                    </label>
                    <label>
                        Due date
                        <input
                            type="date"
                            value={draft.dueDate}
                            onChange={(e) => onDraftChange({ ...draft, dueDate: e.target.value })}
                            aria-label="Due date"
                        />
                    </label>
                    <label className="record-composer__assignee">
                        Assign to
                        <input
                            value={assigneeQuery || draft.assigneeLabel}
                            onChange={(e) => onAssigneeQueryChange(e.target.value)}
                            placeholder="Search users…"
                            aria-label="Assignee"
                        />
                        {assigneeResults.length > 0 ? (
                            <div className="record-composer__assignee-results">
                                {assigneeResults.map((u) => (
                                    <button type="button" key={u.uuid} onClick={() => onPickAssignee(u.uuid, u.label)}>
                                        {u.label}
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </label>
                </div>
                <div className="record-composer__activity-foot">
                    <button
                        type="button"
                        className="record-composer__send"
                        disabled={!valid || sending}
                        onClick={onScheduleActivity}
                    >
                        <Clock size={12} aria-hidden="true" />
                        {sending ? "Scheduling…" : "Schedule"}
                    </button>
                </div>
            </div>
        );
    }

    const suggestions = slashSuggestions(text, canSchedule);
    const mentionsOpen = suggestions.length === 0 && mentionOptions.length > 0;
    return (
        <div className={`record-composer${mode === "note" ? " record-composer--note" : ""}`}>
            {suggestions.length > 0 ? (
                <div className="record-composer__palette" role="listbox" aria-label="Composer commands">
                    {suggestions.map((option, i) => (
                        <button
                            type="button"
                            key={option.token}
                            role="option"
                            aria-selected={i === paletteIndex}
                            className={`record-composer__palette-item${i === paletteIndex ? " is-active" : ""}`}
                            onMouseEnter={() => setPaletteIndex(i)}
                            onClick={() => onTextChange(`${option.token} `)}
                        >
                            <b>{option.token}</b> {option.label}
                            <span>{option.hint}</span>
                        </button>
                    ))}
                </div>
            ) : null}
            {mentionsOpen ? (
                <div className="record-composer__palette" role="listbox" aria-label="Mention suggestions">
                    {mentionOptions.map((option, i) => (
                        <span key={option.key}>
                            {i === 0 || mentionOptions[i - 1].group !== option.group ? (
                                <span className="record-composer__palette-group">
                                    {MENTION_GROUP_LABELS[option.group]}
                                </span>
                            ) : null}
                            <button
                                type="button"
                                role="option"
                                aria-selected={i === paletteIndex}
                                className={`record-composer__palette-item${i === paletteIndex ? " is-active" : ""}`}
                                onMouseEnter={() => setPaletteIndex(i)}
                                onClick={() => onPickMention?.(option)}
                            >
                                <b>{option.label}</b>
                                {option.sub ? ` ${option.sub}` : ""}
                                {option.insert !== FOLLOWERS_ALL_KEY ? <span>@{option.insert}</span> : null}
                            </button>
                        </span>
                    ))}
                </div>
            ) : null}
            <div className="record-composer__bar">
                {mode === "note" ? <Lock size={14} aria-hidden="true" /> : <MessageSquare size={14} aria-hidden="true" />}
                {segments.map((segment, i) =>
                    segment.kind === "text" ? (
                        <span className="record-composer__frozen" key={`t-${i}`}>{segment.text}</span>
                    ) : (
                        <span className="record-composer__chip" key={`c-${i}-${segment.chip.key}`}>
                            @{segment.chip.label}
                            <button
                                type="button"
                                onClick={() => onRemoveSegment?.(i)}
                                aria-label={`Remove mention ${segment.chip.label}`}
                            >
                                <X size={11} />
                            </button>
                        </span>
                    ),
                )}
                <input
                    value={text}
                    onChange={(e) => onTextChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Backspace" && !text && segments.length > 0) {
                            e.preventDefault();
                            onPopSegment?.();
                            return;
                        }
                        if (mentionsOpen) {
                            if (e.key === "ArrowDown") {
                                e.preventDefault();
                                setPaletteIndex((i) => (i + 1) % mentionOptions.length);
                                return;
                            }
                            if (e.key === "ArrowUp") {
                                e.preventDefault();
                                setPaletteIndex((i) => (i - 1 + mentionOptions.length) % mentionOptions.length);
                                return;
                            }
                            if (e.key === "Enter" || e.key === "Tab") {
                                e.preventDefault();
                                onPickMention?.(mentionOptions[Math.min(paletteIndex, mentionOptions.length - 1)]);
                                return;
                            }
                            if (e.key === "Escape") {
                                e.preventDefault();
                                onTextChange(text.replace(/@[^\s@]*$/, ""));
                                return;
                            }
                        }
                        if (suggestions.length > 0) {
                            if (e.key === "ArrowDown") {
                                e.preventDefault();
                                setPaletteIndex((i) => (i + 1) % suggestions.length);
                                return;
                            }
                            if (e.key === "ArrowUp") {
                                e.preventDefault();
                                setPaletteIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
                                return;
                            }
                            if (e.key === "Enter" || e.key === "Tab") {
                                e.preventDefault();
                                const pick = suggestions[Math.min(paletteIndex, suggestions.length - 1)];
                                onTextChange(`${pick.token} `);
                                return;
                            }
                            if (e.key === "Escape") {
                                e.preventDefault();
                                onTextChange("");
                                return;
                            }
                        }
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            onSend();
                        }
                    }}
                    placeholder={
                        mode === "note"
                            ? "Internal note — followers are NOT notified…"
                            : "Write a message… type / for note or activity, @ to mention"
                    }
                    aria-label="Composer"
                    data-viewid="ede-chatter-composer"
                />
                <button
                    type="button"
                    className="record-composer__send"
                    disabled={sending || (!detectComposerMode(text).body.trim() && !segments.some((s) => s.kind === "chip"))}
                    onClick={onSend}
                    aria-label={mode === "note" ? "Log note" : "Send message"}
                    data-viewid="ede-chatter-send"
                >
                    <Send size={12} aria-hidden="true" />
                    {sending ? "…" : mode === "note" ? "Log note" : "Send"}
                </button>
            </div>
            <div className="record-composer__hints">
                <span><b>/note</b> internal note</span>
                {canSchedule ? <span><b>/activity</b> schedule &amp; assign</span> : null}
                <span><b>@</b> mention</span>
            </div>
        </div>
    );
}

interface RecordComposerProps {
    components: string[];
    modelKey: string;
    recordUuid: string;
    /** Form field defs — @mention reads res.partner reference fields. */
    fieldDefs?: FieldDef[];
    /** Fired after a successful post so the timeline + strip refresh. */
    onPosted: () => void;
}

export function RecordComposer({ components, modelKey, recordUuid, fieldDefs, onPosted }: RecordComposerProps): ReactNode {
    const runtime = useRuntime();
    const session = useSession();
    const form = useOptionalFormStateContext();
    const canSchedule = components.includes("ScheduledActivity");
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [activityTypes, setActivityTypes] = useState<ActivityTypeOption[]>([]);
    const [draft, setDraft] = useState<ActivityDraft>({
        typeCode: "",
        subject: "",
        dueDate: "",
        assigneeUuid: "",
        assigneeLabel: "",
    });
    const [assigneeQuery, setAssigneeQuery] = useState("");
    const [assigneeResults, setAssigneeResults] = useState<Array<{ uuid: string; label: string }>>([]);

    const parsed = detectComposerMode(text);
    // /activity only when the component survived gating.
    const mode: ComposerMode = parsed.mode === "activity" && !canSchedule ? "message" : parsed.mode;

    // Entering activity mode: seed subject from any text after the token,
    // default the assignee to me, and lazily load the activity types.
    useEffect(() => {
        if (mode !== "activity") return;
        setDraft((d) => ({
            ...d,
            subject: d.subject || parsed.body,
            assigneeUuid: d.assigneeUuid || session.userId,
            assigneeLabel: d.assigneeLabel || session.name,
        }));
        if (activityTypes.length === 0) {
            void runtime.services.record
                .search("activity.type", { limit: 50, order: "name asc" })
                .then((res) =>
                    setActivityTypes(
                        res.records
                            .map((r) => ({
                                code: typeof r.code === "string" ? r.code : "",
                                name: typeof r.name === "string" && r.name ? r.name : uidLabel(r),
                            }))
                            .filter((t) => t.code),
                    ),
                )
                .catch(() => setActivityTypes([]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    // Debounced assignee search.
    useEffect(() => {
        if (mode !== "activity" || assigneeQuery.trim().length < 2) {
            setAssigneeResults([]);
            return;
        }
        const handle = setTimeout(() => {
            void runtime.services.record
                .search("res.user", { domain: nameSearchDomain(assigneeQuery.trim(), USER_SEARCH_FIELDS) as never, limit: 8 })
                .then((res) =>
                    setAssigneeResults(
                        res.records.map((u) => ({
                            uuid: String(u.record_uuid ?? u.id ?? ""),
                            label: typeof u.name === "string" && u.name ? u.name : uidLabel(u),
                        })),
                    ),
                )
                .catch(() => setAssigneeResults([]));
        }, 250);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assigneeQuery, mode]);

    // ── @mention picker data ────────────────────────────────────────────
    const mQuery = mentionQuery(text);
    const [mentionBase, setMentionBase] = useState<MentionOption[]>([]);   // followers + record fields
    const [mentionDir, setMentionDir] = useState<MentionOption[]>([]);     // users + partners
    const [mentionLoaded, setMentionLoaded] = useState(false);
    const [followersCount, setFollowersCount] = useState(0);

    // Closing the @-session re-arms the loader, so the NEXT "@" refetches a
    // fresh follower list (following mid-session must show up immediately).
    useEffect(() => {
        if (mQuery === null && mentionLoaded) setMentionLoaded(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mQuery === null]);

    // Each "@"-session start: assemble the static groups — followers, then
    // the partners referenced by this record's res.partner fields.
    useEffect(() => {
        if (mQuery === null || mentionLoaded) return;
        setMentionLoaded(true);
        void (async () => {
            const out: MentionOption[] = [];
            try {
                const rows = await runtime.services.chatter.listFollowers(modelKey, recordUuid);
                setFollowersCount(rows.length);
                const userIds = rows.map((r) => (typeof r.user_id === "string" ? r.user_id : null)).filter(Boolean) as string[];
                const partnerIds = rows.map((r) => (typeof r.partner_id === "string" ? r.partner_id : null)).filter(Boolean) as string[];
                const [users, partners] = await Promise.all([
                    userIds.length
                        ? runtime.services.record.search("res.user", { domain: [["record_uuid", "in", userIds]], limit: userIds.length })
                        : Promise.resolve({ records: [] as RecordData[] }),
                    partnerIds.length
                        ? runtime.services.record.search("res.partner", { domain: [["record_uuid", "in", partnerIds]], limit: partnerIds.length })
                        : Promise.resolve({ records: [] as RecordData[] }),
                ]);
                for (const u of users.records) {
                    const name = typeof u.name === "string" ? u.name : uidLabel(u);
                    out.push({
                        key: `f-${u.record_uuid}`,
                        group: "followers",
                        label: name,
                        insert: mentionInsertFor(name, typeof u.email === "string" ? u.email : null),
                    });
                }
                for (const pr of partners.records) {
                    const name = typeof pr.name === "string" ? pr.name : uidLabel(pr);
                    out.push({
                        key: `fp-${pr.record_uuid}`,
                        group: "followers",
                        label: name,
                        sub: "external",
                        insert: mentionInsertFor(name, typeof pr.email === "string" ? pr.email : null),
                    });
                }
            } catch {
                /* group degrades to empty */
            }
            // Partners referenced by THIS record's res.partner fields.
            try {
                const draft = form?.draft ?? {};
                const partnerFields = (fieldDefs ?? []).filter(
                    (f) => f.type === "reference" && f.targetModel === "res.partner",
                );
                const refs: Array<{ uuid: string; fieldLabel: string; display?: string }> = [];
                for (const f of partnerFields) {
                    const v = (draft as Record<string, unknown>)[f.name];
                    if (typeof v === "string" && v) refs.push({ uuid: v, fieldLabel: f.label ?? f.name });
                    else if (v && typeof v === "object" && !Array.isArray(v)) {
                        const obj = v as { id?: unknown; display_name?: unknown };
                        if (typeof obj.id === "string") {
                            refs.push({ uuid: obj.id, fieldLabel: f.label ?? f.name, display: typeof obj.display_name === "string" ? obj.display_name : undefined });
                        }
                    }
                }
                if (refs.length) {
                    const res = await runtime.services.record.search("res.partner", {
                        domain: [["record_uuid", "in", [...new Set(refs.map((r) => r.uuid))]]],
                        limit: refs.length,
                    });
                    const byId = new Map(res.records.map((pr) => [String(pr.record_uuid ?? ""), pr]));
                    const seen = new Set(out.map((o) => o.insert));
                    for (const ref of refs) {
                        const pr = byId.get(ref.uuid);
                        const name = pr && typeof pr.name === "string" ? pr.name : ref.display ?? "";
                        if (!name) continue;
                        const insert = mentionInsertFor(name, pr && typeof pr.email === "string" ? pr.email : null);
                        if (seen.has(insert)) continue;
                        seen.add(insert);
                        out.push({ key: `r-${ref.uuid}-${ref.fieldLabel}`, group: "record", label: name, insert });
                    }
                }
            } catch {
                /* group degrades to empty */
            }
            setMentionBase(out);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mQuery !== null]);

    // Directory search (debounced): internal users first, then partners.
    useEffect(() => {
        if (mQuery === null) {
            setMentionDir([]);
            return;
        }
        const handle = setTimeout(() => {
            void (async () => {
                const out: MentionOption[] = [];
                const q = mQuery.trim();
                try {
                    const users = await runtime.services.record.search("res.user", {
                        domain: (q ? nameSearchDomain(q, USER_SEARCH_FIELDS) : undefined) as never,
                        limit: 5,
                        order: "name asc",
                    });
                    for (const u of users.records) {
                        const name = typeof u.name === "string" ? u.name : uidLabel(u);
                        out.push({ key: `u-${u.record_uuid}`, group: "users", label: name, sub: "internal", insert: mentionInsertFor(name, typeof u.email === "string" ? u.email : null) });
                    }
                } catch { /* skip */ }
                try {
                    const partners = await runtime.services.record.search("res.partner", {
                        domain: (q ? nameSearchDomain(q, PARTNER_SEARCH_FIELDS) : undefined) as never,
                        limit: 5,
                        order: "name asc",
                    });
                    for (const pr of partners.records) {
                        const name = typeof pr.name === "string" ? pr.name : uidLabel(pr);
                        out.push({ key: `p-${pr.record_uuid}`, group: "partners", label: name, insert: mentionInsertFor(name, typeof pr.email === "string" ? pr.email : null) });
                    }
                } catch { /* skip */ }
                setMentionDir(out);
            })();
        }, 250);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mQuery]);

    const [segments, setSegments] = useState<ComposerSegment[]>([]);
    const followerInserts = mentionBase.filter((o) => o.group === "followers").map((o) => o.insert);
    const pickedKeys = new Set(segments.filter((s) => s.kind === "chip").map((s) => (s as { chip: MentionChip }).chip.key));

    const mentionOptions = mQuery === null
        ? []
        : (() => {
              const picked = pickedKeys;
              const base = filterMentionOptions(mentionBase, mQuery).filter((o) => !picked.has(o.key));
              const baseInserts = new Set(base.map((o) => o.insert));
              const dir = filterMentionOptions(mentionDir, mQuery)
                  .filter((o) => !baseInserts.has(o.insert) && !picked.has(o.key));
              // Pinned FIRST: the collective @followers option (user-directed) —
              // shown while the record has followers and it matches the query.
              const all: MentionOption[] = [];
              // Visibility keys off the RAW follower count (not the resolve
              // step), so @followers shows even while names are loading.
              if (
                  followersCount > 0 &&
                  !picked.has(FOLLOWERS_ALL_KEY) &&
                  "followers".startsWith(mQuery.trim().toLowerCase())
              ) {
                  all.push({
                      key: FOLLOWERS_ALL_KEY,
                      group: "followers",
                      label: "followers",
                      sub: `notify all ${followersCount} followers`,
                      insert: FOLLOWERS_ALL_KEY,
                  });
              }
              return [...all, ...base, ...dir];
          })();

    const pickMention = (option: MentionOption) => {
        // Freeze the typed text (minus the trailing "@query") INLINE, then
        // append the chip — "Hi this is test [@hari]" stays in order.
        setSegments((prev) => {
            const frozen = text.replace(/@[^\s@]*$/, "").trimEnd();
            const next = [...prev];
            if (frozen) next.push({ kind: "text", text: frozen });
            next.push({ kind: "chip", chip: { key: option.key, label: option.label, insert: option.insert } });
            return next;
        });
        setText("");
    };

    const popSegment = () => {
        setSegments((prev) => {
            if (prev.length === 0) return prev;
            const last = prev[prev.length - 1];
            const next = prev.slice(0, -1);
            // A text segment re-opens into the input for editing.
            if (last.kind === "text") setText(`${last.text} `);
            return next;
        });
    };

    const reset = () => {
        setText("");
        setSegments([]);
        setDraft({ typeCode: "", subject: "", dueDate: "", assigneeUuid: "", assigneeLabel: "" });
        setAssigneeQuery("");
        setAssigneeResults([]);
    };

    const send = async () => {
        const { mode: m, body } = detectComposerMode(text);
        // Slash mode is detected on the FIRST thing typed — if segments exist
        // the leading text segment carries the mode token instead.
        const head = segments[0];
        const headParse = head?.kind === "text" ? detectComposerMode(head.text) : null;
        const effMode = headParse ? headParse.mode : m;
        const effSegments = headParse && head?.kind === "text"
            ? [{ kind: "text" as const, text: headParse.body }, ...segments.slice(1)]
            : segments;
        const outgoing = assembleBody(effSegments, headParse ? text : body, followerInserts);
        if (!outgoing.trim() || effMode === "activity") return;
        setSending(true);
        try {
            if (effMode === "note") await runtime.services.chatter.postNote(modelKey, recordUuid, outgoing);
            else await runtime.services.chatter.postMessage(modelKey, recordUuid, outgoing);
            reset();
            onPosted();
        } catch {
            /* toast comes from the http layer; keep the draft so nothing is lost */
        } finally {
            setSending(false);
        }
    };

    const scheduleActivity = async () => {
        if (!draft.typeCode || !draft.subject.trim()) return;
        setSending(true);
        try {
            await runtime.services.chatter.scheduleActivity(modelKey, recordUuid, {
                activity_type_code: draft.typeCode,
                subject: draft.subject.trim(),
                due_at: draft.dueDate ? `${draft.dueDate}T17:00:00` : undefined,
                owner_user_id: draft.assigneeUuid || undefined,
            });
            reset();
            onPosted();
        } catch {
            /* keep the panel open with the draft */
        } finally {
            setSending(false);
        }
    };

    return (
        <RecordComposerView
            canSchedule={canSchedule}
            text={text}
            mode={mode}
            sending={sending}
            mentionOptions={mentionOptions}
            onPickMention={pickMention}
            segments={segments}
            onRemoveSegment={(index) => setSegments((prev) => prev.filter((_, i) => i !== index))}
            onPopSegment={popSegment}
            onTextChange={setText}
            onSend={() => void send()}
            onCancelActivity={() => setText("")}
            activityTypes={activityTypes}
            draft={draft}
            onDraftChange={setDraft}
            assigneeQuery={assigneeQuery}
            onAssigneeQueryChange={setAssigneeQuery}
            assigneeResults={assigneeResults}
            onPickAssignee={(uuid, label) => {
                setDraft((d) => ({ ...d, assigneeUuid: uuid, assigneeLabel: label }));
                setAssigneeQuery("");
                setAssigneeResults([]);
            }}
            onScheduleActivity={() => void scheduleActivity()}
        />
    );
}
